"use client";
import { ArtOrderOffer } from "@/api/art-order/types";
import PriceTagIcon from "@/assets/icons/tag-price.svg";
import CalendarIcon from "@/assets/icons/calendar.svg";
import useCryptoInUSD from "@/shared/hooks/useCryptoInUSD";
import usePaymentCurrency from "@/shared/hooks/usePaymentCurrency";
import { toHighDenom } from "@/shared/currencies";
import { useMemo } from "react";
import { twMerge } from "tailwind-merge";

export interface Props {
  artOrderOffer: ArtOrderOffer;
  isAcceptedOffer: boolean;
  paymentErc20TokenAddress: string;
}

export default function ArtOrderPostContent({
  artOrderOffer,
  isAcceptedOffer,
  paymentErc20TokenAddress,
}: Props) {
  const paymentMethod = usePaymentCurrency(paymentErc20TokenAddress);

  const priceInHigh = useMemo(
    () => toHighDenom(artOrderOffer.price, paymentMethod?.decimals),
    [artOrderOffer, paymentMethod],
  );

  const { cryptoInUSD } = useCryptoInUSD(priceInHigh, paymentMethod?.id);

  return (
    <div
      className={twMerge(
        "flex flex-col items-center rounded-2xl gap-5 text-left endemic:border-white endemic:border-2",
        isAcceptedOffer
          ? "bg-[#00A09914] night:bg-endemicGray-1000 endemic:bg-transparent"
          : "bg-endemicGray-50",
      )}
    >
      <p
        className={twMerge(
          "text-body-4 w-full p-4 text-invert light:text-primary",
          isAcceptedOffer && "endemic:text-primary night:text-primary",
        )}
      >
        {artOrderOffer.message}
      </p>
      <div className="flex flex-row justify-center w-full pb-4">
        <div className="flex flex-row gap-4 w-full justify-center">
          <PriceTagIcon
            className={twMerge(
              "w-5 h-5 mt-auto mb-auto",
              isAcceptedOffer && "endemic:fill-white night:fill-white",
            )}
          />
          <div className="flex flex-col">
            <label
              className={twMerge(
                "font-semibold text-black",
                isAcceptedOffer && "endemic:text-primary night:text-primary",
              )}
            >
              {priceInHigh} ${paymentMethod!.label}
            </label>
            <label className="text-endemicGray-700">{cryptoInUSD} USD</label>
          </div>
        </div>
        <div className="flex flex-row gap-4 w-full justify-center">
          <CalendarIcon
            className={twMerge(
              "w-5 h-5 mt-auto mb-auto",
              isAcceptedOffer && "endemic:fill-white night:fill-white",
            )}
          />
          <div className="flex flex-col">
            <label
              className={twMerge(
                "font-semibold text-black",
                isAcceptedOffer && "endemic:text-primary night:text-primary",
              )}
            >
              {artOrderOffer.timeframe} days
            </label>
            <label className="text-endemicGray-700">Timeframe</label>
          </div>
        </div>
      </div>
    </div>
  );
}
